import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Wallet, ExternalLink } from 'lucide-react';
import { useLatestElpris } from '@/hooks/useLatestElpris';
import { UnverifiedBadge } from '@/components/cars/UnverifiedBadge';

interface OwnershipCostBlockProps {
  /** Laveste registrerede nypris i kr. — kun fra prices_dk */
  priceDkk: number | null;
  /** Grøn/CO₂-ejerafgift pr. år i kr. — kun fra variants */
  annualTaxDkk: number | null;
  /** WLTP-forbrug i Wh/km — kun fra powertrain_specs */
  consumptionWhKm: number | null;
  /** Nettobatteri i kWh — kun fra powertrain_specs */
  batteryKwh: number | null;
}

const KM_PER_YEAR = 15000;

function dkk(value: number): string {
  return value.toLocaleString('da-DK', { maximumFractionDigits: 0 });
}

/**
 * Ejeromkostninger baseret udelukkende på databasens tal:
 * nypris, årlig ejerafgift og ladeomkostning ud fra seneste elpris.
 * Felter uden data skjules — der beregnes ingen erstatningstal.
 */
export function OwnershipCostBlock({
  priceDkk,
  annualTaxDkk,
  consumptionWhKm,
  batteryKwh,
}: OwnershipCostBlockProps) {
  const elpris = useLatestElpris('DK1');

  const kwhPrice = elpris?.avg24hDkkKwh ?? elpris?.priceDkkKwh ?? null;
  const kwhPer100Km = consumptionWhKm != null ? (consumptionWhKm * 100) / 1000 : null;
  const costPer100Km = kwhPrice != null && kwhPer100Km != null ? kwhPer100Km * kwhPrice : null;
  const costPerYear = costPer100Km != null ? (costPer100Km * KM_PER_YEAR) / 100 : null;
  const fullChargeCost = kwhPrice != null && batteryKwh != null ? batteryKwh * kwhPrice : null;

  const rows: { label: string; value: string; field?: string; note?: string }[] = [];

  if (priceDkk != null) {
    rows.push({ label: 'Nypris (vejledende)', value: `${dkk(priceDkk)} kr.`, field: 'price_dkk' });
  }
  if (annualTaxDkk != null) {
    rows.push({
      label: 'Årlig ejerafgift',
      value: `${dkk(annualTaxDkk)} kr.`,
      field: 'green_owner_tax_yearly_dkk',
    });
  }
  if (fullChargeCost != null) {
    rows.push({
      label: 'Fuld opladning hjemme',
      value: `${fullChargeCost.toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr.`,
      note: 'rå spotpris uden tariffer og afgifter',
    });
  }
  if (costPer100Km != null) {
    rows.push({
      label: 'Strøm pr. 100 km',
      value: `${costPer100Km.toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr.`,
    });
  }
  if (costPerYear != null) {
    rows.push({
      label: `Strøm pr. år ved ${dkk(KM_PER_YEAR)} km`,
      value: `${dkk(Math.round(costPerYear))} kr.`,
    });
  }

  if (rows.length === 0) return null;

  const hourLabel = elpris?.hourDk
    ? new Date(elpris.hourDk).toLocaleString('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet className="h-4 w-4 text-primary" />
          Pris og ejeromkostninger
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="space-y-0">
          {rows.map((r) => (
            <div
              key={r.label}
              className="flex items-start justify-between gap-3 border-b border-border/50 py-2 last:border-0"
            >
              <dt className="text-sm text-muted-foreground">
                {r.label}
                {r.note ? <span className="block text-xs text-muted-foreground/70">{r.note}</span> : null}
              </dt>
              <dd className="text-right text-sm font-semibold">
                {r.value}
                <UnverifiedBadge field={r.field} />
              </dd>
            </div>
          ))}
        </dl>

        {kwhPrice != null && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            Ladeomkostningen er regnet med en elpris på{' '}
            <span className="font-medium text-foreground">
              {kwhPrice.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr./kWh
            </span>
            {elpris?.avg24hDkkKwh != null ? ' (gennemsnit af de seneste 24 timers spotpris i DK1)' : ' (seneste spotpris i DK1)'}
            {hourLabel ? `, senest opdateret ${hourLabel}` : ''}.{' '}
            {elpris?.sourceUrl ? (
              <a
                href={elpris.sourceUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                Kilde: {elpris.sourceLabel ?? 'Energi Data Service'}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <>Kilde: {elpris.sourceLabel ?? 'Energi Data Service'}.</>
            )}{' '}
            Din faktiske pris inkluderer nettarif, elafgift og moms.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
